import { useEffect, useState, useMemo } from "react";
import { AppBar, Toolbar, Button, Box, CircularProgress, Card, CardContent, CardHeader } from "@mui/material";
import { useSnackbar } from 'notistack';
import logo from '../assets/logo.png';
import { getTermsWithCache } from '../utils/termsCache';
import { useAuth } from "../context/AuthContext";
import POChart from "./POChart";
import POTable from "./POTable";
import axios from "axios";
import { SLEDGEHAMMER_BASE_API } from "../config";

const statusTabs = [
  "PENDING DEPOSIT",
  "IN PRODUCTION",
  "PRODUCTION COMPLETE",
  "AWAITING SHIPMENT",
  "ON THE WATER",
  "AT PORT",
  "RECEIPTING AT 3PL"
];

const Dashboard = () => {
  const [poData, setPoData] = useState([]);
  const [load, setLoad] = useState(true);
  const [terms, setTerms] = useState({});
  const [showTable, setShowTable] = useState(false);

  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const baseUrl = SLEDGEHAMMER_BASE_API;

  // Get the deposit date from prepayments (first prepayment date)
  const getDepositDate = (data) => {
    if (data.depositDate && data.depositDate !== '-') {
      return data.depositDate;
    }
    if (data.prepayments && data.prepayments.length > 0) {
      const firstPrepay = data.prepayments[0];
      if (firstPrepay.date) {
        return firstPrepay.date;
      }
    }
    return null;
  };

  useEffect(() => {

    const fetchPOs = async () => {
      try {
        const termData = await getTermsWithCache();
        setTerms(termData);
        const res = await axios.get(`${baseUrl}/dashboard/po`);
        const data = res.data;
        const rawData = Array.isArray(data) ? data : [];
        
        const actualDateFields = [
          'artworkActual', 'midprodActual', 'pendingPtsActual',
          'inspectionApprovedActual', 'freightBookedActual',
          'prodCompleteActual', 'crdActual', 'rtsActual', 'etdActual', 'etaPortActual',
          'lfdPortActual', 'arrival3PLActual'
        ];
        const hasDate = (val) => val && val !== '-';

        const processedData = rawData.map((po, index) => {
          const row = { ...po, _rowId: index };
          const depositDate = getDepositDate(row);
          if (depositDate && (!row.depositDate || row.depositDate === '-')) {
            row.depositDate = depositDate;
          }
          const noDeposit = !hasDate(row.depositDate);
          const noOtherDates = actualDateFields.every((f) => !hasDate(row[f]));
          if (noDeposit && noOtherDates) {
            row.status = "PENDING DEPOSIT";
          } else if (row.lineProgressStatus) {
            row.status = row.lineProgressStatus;
          }
          return row;
        });
        
        setPoData(processedData);
      } catch (err) {
        console.error("Failed to fetch POs", err);
        setPoData([]);
      } finally {
        setLoad(false);
      }
    };

    fetchPOs();
  }, []);

  const statusCounts = useMemo(() => {
    const counts = {};
    statusTabs.forEach((status) => {
      counts[status] = poData.filter((po) => po.status === status).length;
    });
    const total = poData.length || 1;
    const percentages = {};
    Object.entries(counts).forEach(([status, count]) => {
      percentages[status] = Math.round((count / total) * 100);
    });
    return { counts, percentages };
  }, [poData]);

  return (
    <>
      <AppBar position="static" sx={{ mb: 2, backgroundColor: '#23324c' }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box component="img" src={logo} alt="Logo" sx={{ height: 40 }} />
          <Button color="inherit" onClick={logout}>Logout</Button>
        </Toolbar>
      </AppBar>

      {load ? (
        <Box textAlign="center" mt={5}><CircularProgress /></Box>
      ) : showTable ? (
        <Box p={2}>
          <POTable
            poData={poData}
            statusTabs={statusTabs}
            terms={terms}
            setPoData={setPoData}
            enqueueSnackbar={enqueueSnackbar}
          />
        </Box>
      ) : (
        <Box p={2}>
          <Box marginTop={2}>
            <Card>
              <CardHeader
                title="Purchase Orders Overview"
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '& .MuiCardHeader-title': { color: 'white', fontWeight: 'bold' }
                }}
              />
              <CardContent>
                <POChart
                  poData={poData}
                  statusTabs={statusTabs}
                  statusCounts={statusCounts}
                  onShowTable={() => setShowTable(true)}
                />
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}
    </>
  );
};

export default Dashboard;
